import React, { useEffect, useState } from 'react';
import { Box, Typography, Container, Grid, useTheme, Paper, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Chip, IconButton, Tooltip, Button, TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem, Alert, ToggleButtonGroup, ToggleButton, Divider } from '@mui/material';
import { Icon } from '@iconify/react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import { VALID_MTOS } from '../utils/constants';
import { transactionStore, HolidayCalendarChange } from '../utils/transactionStore';
interface Holiday {
  id: string;
  name: string;
  date: string;
  scope: 'global' | 'partner' | 'country';
  partner?: string;
  country?: string;
  notes?: string;
  tier: 1 | 2;
}
const COUNTRY_HOLIDAYS: Holiday[] = [{
  id: 'c1',
  name: 'UAE National Day',
  date: '2026-12-02',
  scope: 'country',
  country: 'UAE',
  tier: 1
}, {
  id: 'c2',
  name: 'UAE National Day (2)',
  date: '2026-12-03',
  scope: 'country',
  country: 'UAE',
  tier: 1
}, {
  id: 'c3',
  name: 'Saudi National Day',
  date: '2026-09-23',
  scope: 'country',
  country: 'Saudi Arabia',
  tier: 1
}, {
  id: 'c4',
  name: 'UK Christmas Day',
  date: '2026-12-25',
  scope: 'country',
  country: 'United Kingdom',
  tier: 1
}, {
  id: 'c5',
  name: 'UK Boxing Day',
  date: '2026-12-26',
  scope: 'country',
  country: 'United Kingdom',
  tier: 1
}, {
  id: 'c6',
  name: 'Qatar National Day',
  date: '2026-12-18',
  scope: 'country',
  country: 'Qatar',
  tier: 1
}, {
  id: 'c7',
  name: 'Kuwait National Day',
  date: '2026-02-25',
  scope: 'country',
  country: 'Kuwait',
  tier: 1
}, {
  id: 'c8',
  name: 'Eid Al Fitr',
  date: '2026-03-31',
  scope: 'country',
  country: 'UAE',
  tier: 1
}, {
  id: 'c9',
  name: 'Eid Al Adha',
  date: '2026-06-07',
  scope: 'country',
  country: 'UAE',
  tier: 1
}];
const ADDED_HOLIDAYS_SEED: Holiday[] = [{
  id: 'a1',
  name: 'BIPL Internal Closure',
  date: '2026-08-14',
  scope: 'global',
  notes: 'Pakistan Independence Day',
  tier: 2
}, {
  id: 'a2',
  name: 'RIA Extended Holiday',
  date: '2026-12-31',
  scope: 'partner',
  partner: 'RIA Money Transfer',
  notes: 'Per partner SLA',
  tier: 2
}, {
  id: 'a3',
  name: 'Year-End Bank Holiday',
  date: '2026-12-30',
  scope: 'global',
  notes: '',
  tier: 2
}];
const PARTNER_COUNTRY_MAP: Record<string, string> = {
  'RIA Money Transfer': 'UAE',
  'IME (International Money Express)': 'Saudi Arabia',
  'InstantCash': 'United Kingdom',
  'Express Money': 'Qatar',
  'Jingle Pay': 'Kuwait'
};
type ViewMode = 'global' | string; // string = partner name

interface Props {
  onLogout: () => void;
}
const SCOPE_CHIP: Record<string, {
  label: string;
  bg: string;
  color: string;
}> = {
  global: {
    label: 'Global',
    bg: 'rgba(91,78,240,0.12)',
    color: '#5B4EF0'
  },
  partner: {
    label: 'Partner',
    bg: 'rgba(16,185,129,0.12)',
    color: '#059669'
  },
  country: {
    label: 'Country',
    bg: 'rgba(107,114,128,0.10)',
    color: '#6B7280'
  }
};
const REQUEST_STATUS_CHIP: Record<string, {
  label: string;
  bg: string;
  color: string;
}> = {
  PENDING_CHECKER: {
    label: 'Pending',
    bg: 'rgba(245,158,11,0.15)',
    color: '#D97706'
  },
  REJECTED: {
    label: 'Rejected',
    bg: 'rgba(239,68,68,0.12)',
    color: '#EF4444'
  }
};
const HolidayCalendar: React.FC<Props> = ({
  onLogout
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [added, setAdded] = useState<Holiday[]>(ADDED_HOLIDAYS_SEED);
  const [pendingRequests, setPendingRequests] = useState<HolidayCalendarChange[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('global');
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Holiday | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Holiday | null>(null);

  // Form state
  const [form, setForm] = useState({
    name: '',
    date: '',
    scope: 'global' as 'global' | 'partner',
    partner: '',
    notes: ''
  });
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  useEffect(() => {
    const sync = () => {
      const all = transactionStore.getAllHolidayCalendarChanges();
      const approvedAdds = all.filter(c => c.action === 'ADD' && c.status === 'APPROVED');
      approvedAdds.forEach(c => {
        setAdded(prev => prev.some(h => h.id === `add-${c.id}`) ? prev : [...prev, {
          id: `add-${c.id}`,
          name: c.name,
          date: c.date,
          scope: c.scope,
          partner: c.scope === 'partner' ? c.partner : undefined,
          notes: c.notes,
          tier: 2
        }]);
      });
      const approvedEdits = all.filter(c => c.action === 'EDIT' && c.status === 'APPROVED');
      approvedEdits.forEach(c => {
        if (!c.holidayId) return;
        setAdded(prev => prev.map(h => h.id === c.holidayId ? {
          ...h,
          name: c.name,
          date: c.date,
          scope: c.scope,
          partner: c.scope === 'partner' ? c.partner : undefined,
          notes: c.notes
        } : h));
      });
      const approvedDeleteIds = all.filter(c => c.action === 'DELETE' && c.status === 'APPROVED').map(c => c.holidayId);
      if (approvedDeleteIds.length) {
        setAdded(prev => prev.filter(h => !approvedDeleteIds.includes(h.id)));
      }
      setPendingRequests(all.filter(c => c.status === 'PENDING_CHECKER' || c.status === 'REJECTED'));
    };
    sync();
    const unsubscribe = transactionStore.subscribe(sync);
    return unsubscribe;
  }, []);

  // ── Filtering ─────────────────────────────────────────────────────────────
  const visibleCountry = viewMode === 'global' ? [] : COUNTRY_HOLIDAYS.filter(h => h.country === PARTNER_COUNTRY_MAP[viewMode]);
  const visibleAdded = added.filter(h => {
    if (viewMode === 'global') return true;
    if (h.scope === 'global') return true;
    if (h.scope === 'partner' && h.partner === viewMode) return true;
    return false;
  });
  const allRows = [...visibleCountry, ...visibleAdded].filter(h => !search || h.name.toLowerCase().includes(search.toLowerCase()) || h.date.includes(search)).sort((a, b) => a.date.localeCompare(b.date));

  // ── Add / Edit ─────────────────────────────────────────────────────────────
  const openAdd = () => {
    setForm({
      name: '',
      date: '',
      scope: 'global',
      partner: '',
      notes: ''
    });
    setEditTarget(null);
    setAddOpen(true);
  };
  const openEdit = (h: Holiday) => {
    setForm({
      name: h.name,
      date: h.date,
      scope: h.scope as 'global' | 'partner',
      partner: h.partner || '',
      notes: h.notes || ''
    });
    setEditTarget(h);
    setAddOpen(true);
  };
  const handleSave = () => {
    if (!form.name.trim() || !form.date) return;
    const payload = {
      action: (editTarget ? 'EDIT' : 'ADD') as 'ADD' | 'EDIT',
      holidayId: editTarget?.id,
      name: form.name,
      date: form.date,
      scope: form.scope,
      partner: form.scope === 'partner' ? form.partner : undefined,
      notes: form.notes
    };
    transactionStore.submitHolidayCalendarChange(payload, `${editTarget ? 'Edit' : 'Addition'} of holiday "${form.name}" requested via Holiday Calendar`);
    setAddOpen(false);
  };
  const handleDelete = () => {
    if (!deleteTarget) return;
    transactionStore.submitHolidayCalendarChange({
      action: 'DELETE',
      holidayId: deleteTarget.id,
      name: deleteTarget.name,
      date: deleteTarget.date,
      scope: deleteTarget.scope as 'global' | 'partner',
      partner: deleteTarget.partner,
      notes: deleteTarget.notes
    }, `Deletion of holiday "${deleteTarget.name}" requested via Holiday Calendar`);
    setDeleteTarget(null);
  };
  const statusForHoliday = (holidayId: string) => {
    const latest = pendingRequests.filter(r => r.holidayId === holidayId).slice(-1)[0];
    return latest ? REQUEST_STATUS_CHIP[latest.status] : null;
  };

  // ── Counts for toggle labels ───────────────────────────────────────────────
  const globalCount = added.filter(h => h.scope === 'global').length + added.filter(h => h.scope === 'partner').length;
  return <Box sx={{
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: isDarkMode ? '#0F0E24' : '#F8FAFC'
  }} data-id="element-3626" data-mp="3ahj01ja55w6n">
      <TopBar onMenuClick={() => setMobileOpen(!mobileOpen)} onLogout={onLogout} data-id="element-3627" data-mp="3ohk2322579np" />
      <Sidebar open={mobileOpen} onClose={() => setMobileOpen(false)} activePage="holiday-calendar" data-id="element-3628" data-mp="3kfozzvce0s8v" />

      <Box component="main" sx={{
      flexGrow: 1,
      p: 3,
      width: {
        sm: 'calc(100% - 240px)'
      },
      mt: 8
    }} data-id="element-3629" data-mp="29kgpxa3zdmi">
        <Container maxWidth="xl" data-id="element-3630" data-mp="3tgeqcjivpqf7">

          {/* ── Header ── */}
          <Grid container spacing={2} alignItems="flex-start" sx={{
          mb: 3
        }} data-id="element-3631" data-mp="3te0y0ifl2sbc">
            <Grid item xs={12} md={7} data-id="element-3632" data-mp="bz40d55nbbif">
              <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              mb: 1
            }} data-id="element-3633" data-mp="70ka4sk0rdk9">
                <Icon icon="lucide:calendar-days" width={24} color="#5B4EF0" data-id="element-3634" data-mp="72wq5067vduo" />
                <Box data-id="element-3635" data-mp="l0lc6bc0sr1b">
                  <Typography variant="h5" fontWeight={800} data-id="element-3636" data-mp="kxz3u9hb27i1">Holiday Calendar</Typography>
                  <Typography variant="body2" color="text.secondary" data-id="element-3637" data-mp="fz5rwlho2ojz">
                    Manage added holidays for auto-cancellation trigger (BRD §19)
                  </Typography>
                </Box>
              </Box>
              <Alert severity="info" icon={<Icon icon="lucide:info" width={16} data-id="element-3639" data-mp="qm2nwp1dtlrv" />} sx={{
              borderRadius: 2,
              fontSize: '0.82rem',
              py: 0.5
            }} data-id="element-3638" data-mp="qmccq9aoxmvz">
                Standard country holidays are auto-applied from each partner's registered location and are read-only.
                Additions, edits, and deletions here are sent to the checker for approval.
              </Alert>
            </Grid>
            <Grid item xs={12} md={5} sx={{
            display: 'flex',
            justifyContent: {
              md: 'flex-end'
            },
            alignItems: 'flex-start',
            gap: 1.5,
            flexWrap: 'wrap'
          }} data-id="element-3640" data-mp="6zxyuupwyarl">
              <TextField size="small" placeholder="Search holidays…" value={search} onChange={e => setSearch(e.target.value)} InputProps={{
              startAdornment: <InputAdornment position="start" data-id="element-3642" data-mp="3jeymw2ehb5d6"><Icon icon="lucide:search" width={15} color="#9CA3AF" data-id="element-3643" data-mp="3ji2wxgemkvbr" /></InputAdornment>,
              endAdornment: search ? <InputAdornment position="end" data-id="element-3644" data-mp="3aflcn7ij6cre">
                      <IconButton size="small" onClick={() => setSearch('')} data-id="element-3645" data-mp="3ah4rctojgfr2"><Icon icon="lucide:x" width={13} data-id="element-3646" data-mp="3fdd2zx3j1qth" /></IconButton>
                    </InputAdornment> : null
            }} sx={{
              width: 220,
              bgcolor: 'background.paper',
              borderRadius: 2
            }} data-id="element-3641" data-mp="3u3b5d7s76jm2" />
              <Button variant="contained" startIcon={<Icon icon="lucide:plus" width={15} data-id="element-3648" data-mp="3td4muegq61fe" />} onClick={openAdd} sx={{
              background: 'linear-gradient(135deg, #5B4EF0 0%, #00B4B3 100%)',
              fontWeight: 700,
              borderRadius: 2,
              whiteSpace: 'nowrap'
            }} data-id="element-3647" data-mp="b210t1wse0ft">
                Add Holiday
              </Button>
            </Grid>
          </Grid>

          {/* ── View Toggle ── */}
          <Box sx={{
          mb: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap'
        }} data-id="element-3649" data-mp="1427hvgadk9wg">
            <Typography fontWeight={700} fontSize="0.8rem" color="text.secondary" sx={{
            textTransform: 'uppercase',
            letterSpacing: 0.5
          }} data-id="element-3650" data-mp="101npeq0qxugn">
              View:
            </Typography>
            <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)} size="small" sx={{
            '& .MuiToggleButton-root': {
              px: 2,
              py: 0.6,
              fontSize: '0.8rem',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '8px !important',
              border: `1px solid ${theme.palette.divider}`,
              mx: 0.25
            },
            '& .Mui-selected': {
              bgcolor: 'rgba(91,78,240,0.12) !important',
              color: '#5B4EF0 !important',
              borderColor: 'rgba(91,78,240,0.3) !important'
            }
          }} data-id="element-3651" data-mp="18tpmqffiqump">
              <ToggleButton value="global" data-id="element-3652" data-mp="21i3mazm99z0d">🌐 Global ({globalCount})</ToggleButton>
              {VALID_MTOS.map(p => <ToggleButton key={p} value={p} data-id="element-3653" data-mp="29jcb5o77risc">{p.split(' ')[0]}</ToggleButton>)}
            </ToggleButtonGroup>
          </Box>

          {/* ── Legend ── */}
          <Box sx={{
          display: 'flex',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap'
        }} data-id="element-3654" data-mp="2ijhixzgbkj71">
            {Object.entries(SCOPE_CHIP).map(([key, val]) => <Box key={key} sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75
          }} data-id="element-3655" data-mp="2nhj0gfy3kage">
                <Box sx={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: val.color
            }} data-id="element-3656" data-mp="1pvlbq0t4j8w0" />
                <Typography fontSize="0.78rem" color="text.secondary" data-id="element-3657" data-mp="1utdfpt71mlgk">{val.label}</Typography>
              </Box>)}
            <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75
          }} data-id="element-3658" data-mp="1zs7wyqpd41yk">
              <Icon icon="lucide:lock" width={11} color="#9CA3AF" data-id="element-3659" data-mp="24jzob3gcebpr" />
              <Typography fontSize="0.78rem" color="text.secondary" data-id="element-3660" data-mp="174c2lsn9mm4y">Read-only (country standard)</Typography>
            </Box>
          </Box>

          {/* ── Table ── */}
          <TableContainer component={Paper} elevation={0} sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
          overflow: 'hidden',
          mb: 4
        }} data-id="element-3661" data-mp="1c1knw23pn6af">
            <Table data-id="element-3662" data-mp="zkjmzfyh1nhp">
              <TableHead data-id="element-3663" data-mp="zm4fl0186wcx">
                <TableRow sx={{
                bgcolor: isDarkMode ? 'rgba(91,78,240,0.08)' : 'rgba(91,78,240,0.04)'
              }} data-id="element-3664" data-mp="ziqb9m904d2u">
                  {['Date', 'Holiday Name', 'Type', 'Scope / Partner', 'Notes', ''].map(h => <TableCell key={h} sx={{
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  py: 1.5
                }} data-id="element-3665" data-mp="poq3fcmc4tqf">
                      {h}
                    </TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody data-id="element-3666" data-mp="bt4grc3kqm16">
                {allRows.length === 0 ? <TableRow data-id="element-3667" data-mp="6xgscywx1wwq">
                    <TableCell colSpan={6} data-id="element-3668" data-mp="6uazr9p2wcw9">
                      <Box sx={{
                    py: 5,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1.5
                  }} data-id="element-3669" data-mp="1xau2zw55c5o">
                        <Icon icon="lucide:calendar-x" width={36} color="#9CA3AF" data-id="element-3670" data-mp="3u24w0sy54f9c" />
                        <Typography color="text.secondary" fontWeight={600} data-id="element-3671" data-mp="mn4yzkg5ue06">No holidays found</Typography>
                        <Button size="small" variant="outlined" onClick={openAdd} startIcon={<Icon icon="lucide:plus" width={13} data-id="element-3673" data-mp="du1n3gl5y97n" />} sx={{
                      borderRadius: 2,
                      mt: 0.5
                    }} data-id="element-3672" data-mp="rk55txku3ksc">
                          Add First Holiday
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow> : allRows.map((h, i) => {
                const scopeStyle = SCOPE_CHIP[h.scope];
                const isCountry = h.tier === 1;
                const reqChip = !isCountry ? statusForHoliday(h.id) : null;
                return <TableRow key={h.id} sx={{
                  bgcolor: isCountry ? isDarkMode ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.015)' : i % 2 === 0 ? 'transparent' : isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                  '&:hover': {
                    bgcolor: isDarkMode ? 'rgba(91,78,240,0.05)' : 'rgba(91,78,240,0.03)'
                  },
                  opacity: isCountry ? 0.8 : 1
                }} data-id="element-3674" data-mp="3v1598l3sj79r" data-mpi={i}>
                      <TableCell data-id="element-3675" data-mp="3nk8r5vmxmvaf">
                        <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }} data-id="element-3676" data-mp="3irfk313zrav6">
                          <Icon icon="lucide:calendar" width={14} color={scopeStyle.color} data-id="element-3677" data-mp="1axrvy7qob4e" />
                          <Typography fontWeight={600} fontSize="0.875rem" sx={{
                        fontVariantNumeric: 'tabular-nums'
                      }} data-id="element-3678" data-mp="3shr03m0y9aif">
                            {h.date}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell data-id="element-3679" data-mp="5dkkw2g5c236">
                        <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75
                    }} data-id="element-3680" data-mp="2c390f4g8goii">
                          {isCountry && <Icon icon="lucide:lock" width={12} color="#9CA3AF" data-id="element-3681" data-mp="274e55312x7hi" />}
                          <Typography fontWeight={isCountry ? 500 : 700} fontSize="0.875rem" color={isCountry ? 'text.secondary' : 'text.primary'} data-id="element-3682" data-mp="276iiebkvbiug">
                            {h.name}
                          </Typography>
                          {reqChip && <Chip label={reqChip.label} size="small" sx={{
                        bgcolor: reqChip.bg,
                        color: reqChip.color,
                        fontWeight: 700,
                        fontSize: '0.65rem'
                      }} data-id="element-3683" data-mp="kyxn9jg8i39p" />}
                        </Box>
                      </TableCell>
                      <TableCell data-id="element-3684" data-mp="tz2i6u48r96z">
                        <Chip label={scopeStyle.label} size="small" sx={{
                      bgcolor: scopeStyle.bg,
                      color: scopeStyle.color,
                      fontWeight: 700,
                      fontSize: '0.72rem'
                    }} data-id="element-3685" data-mp="txrcgor8jrd8" />
                      </TableCell>
                      <TableCell data-id="element-3686" data-mp="18qupcitvtgxm">
                        <Typography fontSize="0.82rem" color="text.secondary" data-id="element-3687" data-mp="1cqvb6ojb8uep">
                          {h.tier === 1 ? h.country || '—' : h.scope === 'partner' ? h.partner : 'All Partners'}
                        </Typography>
                      </TableCell>
                      <TableCell data-id="element-3688" data-mp="3254dnqba1c9y">
                        <Typography fontSize="0.8rem" color="text.secondary" sx={{
                      maxWidth: 200,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }} data-id="element-3689" data-mp="2x7w248kmw6ho">
                          {h.notes || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" data-id="element-3690" data-mp="3kxqhawsfe753">
                        {!isCountry && <Box sx={{
                      display: 'flex',
                      gap: 0.5,
                      justifyContent: 'flex-end'
                    }} data-id="element-3691" data-mp="3gwd8cdu1dnty">
                            <Tooltip title="Edit" data-id="element-3692" data-mp="3c5oit41x30xg">
                              <IconButton size="small" onClick={() => openEdit(h)} sx={{
                          color: '#5B4EF0'
                        }} data-id="element-3693" data-mp="3c4de920ndp5a">
                                <Icon icon="lucide:pencil" width={15} data-id="element-3694" data-mp="37623mp2j3am0" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete" data-id="element-3695" data-mp="3q4qq8czfojz">
                              <IconButton size="small" onClick={() => setDeleteTarget(h)} sx={{
                          color: '#EF4444',
                          '&:hover': {
                            bgcolor: 'rgba(239,68,68,0.08)'
                          }
                        }} data-id="element-3696" data-mp="3ojxqn6bgdo8">
                                <Icon icon="lucide:trash-2" width={15} data-id="element-3697" data-mp="3czfzyxky9tb1" />
                              </IconButton>
                            </Tooltip>
                          </Box>}
                      </TableCell>
                    </TableRow>;
              })}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography fontWeight={800} fontSize="0.95rem" sx={{
          mb: 1.5
        }} data-id="element-3698" data-mp="3vx0h1ihh3nl3">Pending Holiday Requests</Typography>
          {pendingRequests.length === 0 ? <Paper elevation={0} sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
          p: 3,
          textAlign: 'center'
        }} data-id="element-3699" data-mp="3hx7nlfekat9f">
              <Typography variant="body2" color="text.secondary" data-id="element-3700" data-mp="26ikfz4pcx2w5">No pending or rejected holiday requests.</Typography>
            </Paper> : <TableContainer component={Paper} elevation={0} sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 3,
          overflow: 'hidden'
        }} data-id="element-3701" data-mp="2alh2h6i0pj5d">
              <Table data-id="element-3702" data-mp="1y1tjhqs9i2g6">
                <TableHead data-id="element-3703" data-mp="1y0a402efjvfg">
                  <TableRow sx={{
                bgcolor: isDarkMode ? 'rgba(245,158,11,0.08)' : 'rgba(245,158,11,0.05)'
              }} data-id="element-3704" data-mp="1y120rgjrjz7b">
                    {['Action', 'Holiday Name', 'Date', 'Requested By', 'Requested At', 'Status'].map(h => <TableCell key={h} sx={{
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  py: 1.5
                }} data-id="element-3705" data-mp="27xn6y57irgcq">{h}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody data-id="element-3706" data-mp="1u0i042zzedgf">
                  {pendingRequests.map(r => <TableRow key={r.id} data-id="element-3707" data-mp="1p8q77ltcrvn4">
                      <TableCell data-id="element-3708" data-mp="1p6x1q9uh7hgp"><Chip label={r.action} size="small" sx={{
                    fontWeight: 700
                  }} data-id="element-3709" data-mp="1p75fs52c4sth" /></TableCell>
                      <TableCell data-id="element-3710" data-mp="11h2pnuhzdwxi"><Typography fontWeight={700} fontSize="0.875rem" data-id="element-3711" data-mp="wkcb5bltr9e7">{r.name}</Typography></TableCell>
                      <TableCell data-id="element-3712" data-mp="2ergaf7l6dvzp"><Typography fontSize="0.8rem" data-id="element-3713" data-mp="2er6r22umyn2z">{r.date}</Typography></TableCell>
                      <TableCell data-id="element-3714" data-mp="29sc18gy93y9d"><Typography fontSize="0.8rem" data-id="element-3715" data-mp="29slt6a2uwfhp">{r.requestedBy}</Typography></TableCell>
                      <TableCell data-id="element-3716" data-mp="2njqjumo8xxfw"><Typography fontSize="0.8rem" data-id="element-3717" data-mp="2nji8x0bop27c">{new Date(r.requestedAt).toLocaleString()}</Typography></TableCell>
                      <TableCell data-id="element-3718" data-mp="2iss4bpb313dp"><Chip label={REQUEST_STATUS_CHIP[r.status]?.label || r.status} size="small" sx={{
                    bgcolor: REQUEST_STATUS_CHIP[r.status]?.bg,
                    color: REQUEST_STATUS_CHIP[r.status]?.color,
                    fontWeight: 700
                  }} data-id="element-3719" data-mp="2irz0aw3icrz9" /></TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </TableContainer>}

          {/* ── Row count ── */}
          <Typography variant="caption" color="text.secondary" sx={{
          mt: 1.5,
          display: 'block'
        }} data-id="element-3720" data-mp="32i2dekgboq2u">
            {allRows.length} holiday{allRows.length !== 1 ? 's' : ''} shown
            {viewMode !== 'global' ? ` for ${viewMode}` : ' (global view)'}
          </Typography>
        </Container>
      </Box>

      {/* ── Add / Edit Dialog ── */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth PaperProps={{
      sx: {
        borderRadius: 3
      }
    }} data-id="element-3721" data-mp="y1fey77mdpwp">
        <DialogTitle sx={{
        fontWeight: 800,
        pb: 0.5
      }} data-id="element-3722" data-mp="o9rskkpx6z23">
          {editTarget ? 'Edit Holiday' : 'Add Holiday'}
        </DialogTitle>
        <DialogContent sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        pt: '16px !important'
      }} data-id="element-3723" data-mp="t71nugzzq4xu">

          <TextField label="Holiday Name" fullWidth size="small" value={form.name} onChange={e => setForm(p => ({
          ...p,
          name: e.target.value
        }))} placeholder="e.g. BIPL Internal Closure" data-id="element-3724" data-mp="e9iv73k6z7l4" />

          <TextField label="Date" type="date" fullWidth size="small" value={form.date} onChange={e => setForm(p => ({
          ...p,
          date: e.target.value
        }))} InputLabelProps={{
          shrink: true
        }} data-id="element-3725" data-mp="a8yyg13j65zi" />

          <FormControl size="small" fullWidth data-id="element-3726" data-mp="228uz753n5d8h">
            <InputLabel data-id="element-3727" data-mp="229wdrjjv8huy">Scope</InputLabel>
            <Select label="Scope" value={form.scope} onChange={e => setForm(p => ({
            ...p,
            scope: e.target.value as 'global' | 'partner',
            partner: ''
          }))} data-id="element-3728" data-mp="226i8a2fwnsja">
              <MenuItem value="global" data-id="element-3729" data-mp="1o921gptp5wr0">
                <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }} data-id="element-3730" data-mp="1oa3nfpc943ni">
                  <Icon icon="lucide:globe" width={15} color="#5B4EF0" data-id="element-3731" data-mp="2lxc1x0du4cg0" />
                  <Box data-id="element-3732" data-mp="2lynarem50adz">
                    <Typography fontSize="0.875rem" fontWeight={600} data-id="element-3733" data-mp="2gx7ndfwhlrsf">Global</Typography>
                    <Typography fontSize="0.75rem" color="text.secondary" data-id="element-3734" data-mp="2c6r9xvso2662">Applies to all partners</Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem value="partner" data-id="element-3735" data-mp="2763i57wm5las">
                <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }} data-id="element-3736" data-mp="34nldv1zfbikw">
                  <Icon icon="lucide:handshake" width={15} color="#059669" data-id="element-3737" data-mp="2zwv9ns2qhlrs" />
                  <Box data-id="element-3738" data-mp="2zusbulvcaoci">
                    <Typography fontSize="0.875rem" fontWeight={600} data-id="element-3739" data-mp="377zzrfz0p4ji">Partner-Specific</Typography>
                    <Typography fontSize="0.75rem" color="text.secondary" data-id="element-3740" data-mp="3c6l1jjtx3wa2">Applies to one partner only</Typography>
                  </Box>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {form.scope === 'partner' && <FormControl size="small" fullWidth data-id="element-3741" data-mp="3m2xtod9te22p">
              <InputLabel data-id="element-3742" data-mp="3q7ds5th3m77w">Partner</InputLabel>
              <Select label="Partner" value={form.partner} onChange={e => setForm(p => ({
            ...p,
            partner: e.target.value
          }))} data-id="element-3743" data-mp="3q4jag9pgz0i6">
                {VALID_MTOS.map(m => <MenuItem key={m} value={m} data-id="element-3744" data-mp="8pul0b0pm0u4">{m}</MenuItem>)}
              </Select>
            </FormControl>}

          <TextField label="Notes (optional)" fullWidth size="small" multiline rows={2} value={form.notes} onChange={e => setForm(p => ({
          ...p,
          notes: e.target.value
        }))} placeholder="e.g. Per partner SLA agreement ref #2026-BIPL-012" data-id="element-3745" data-mp="dqiautd7cbmr" />

          <Alert severity="info" icon={<Icon icon="lucide:clock" width={16} data-id="element-3747" data-mp="1aerjfjfhrknl" />} sx={{
          borderRadius: 2,
          fontSize: '0.82rem',
          py: 0.5
        }} data-id="element-3746" data-mp="1ag2n7j8xssep">
            This will be sent to the checker for approval before it takes effect.
          </Alert>
          {form.scope === 'global' && <Alert severity="warning" icon={<Icon icon="lucide:triangle-alert" width={16} data-id="element-3749" data-mp="wh2yycpc6fj2" />} sx={{
          borderRadius: 2,
          fontSize: '0.82rem',
          py: 0.5
        }} data-id="element-3748" data-mp="whuigp5nsmsz">
              Global holidays apply to all partners and will trigger auto-cancellation logic system-wide on this date once approved.
            </Alert>}
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2.5,
        gap: 1
      }} data-id="element-3750" data-mp="10kitn3hui5t4">
          <Button onClick={() => setAddOpen(false)} sx={{
          borderRadius: 2
        }} data-id="element-3751" data-mp="2gvmtajhdeuor">Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name.trim() || !form.date || form.scope === 'partner' && !form.partner} sx={{
          background: 'linear-gradient(135deg, #5B4EF0 0%, #00B4B3 100%)',
          fontWeight: 700,
          borderRadius: 2,
          px: 3
        }} data-id="element-3752" data-mp="2bycvogjtvcpu">
            {editTarget ? 'Send for Approval' : 'Send for Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm ── */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth PaperProps={{
      sx: {
        borderRadius: 3
      }
    }} data-id="element-3753" data-mp="335bmbn2n9t9v">
        <DialogTitle sx={{
        fontWeight: 800
      }} data-id="element-3754" data-mp="383mza6o8kjwb">Request Holiday Deletion?</DialogTitle>
        <DialogContent data-id="element-3755" data-mp="2te8r31wec8ii">
          <Typography variant="body2" color="text.secondary" data-id="element-3756" data-mp="2tf09tc4wabrd">
            A deletion request for <strong data-id="element-3757" data-mp="2yd212kch5hdx">{deleteTarget?.name}</strong> ({deleteTarget?.date}) will be sent to the checker for approval. It stays active until then.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2.5,
        gap: 1
      }} data-id="element-3758" data-mp="3qvphhmgj088o">
          <Button onClick={() => setDeleteTarget(null)} sx={{
          borderRadius: 2
        }} data-id="element-3759" data-mp="4l8lw1ma5grq">Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} sx={{
          fontWeight: 700,
          borderRadius: 2
        }} data-id="element-3760" data-mp="3vrjtyhppjhsk">Send for Approval</Button>
        </DialogActions>
      </Dialog>
    </Box>;
};
export default HolidayCalendar;